import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';

import { CertificationProvider } from 'app/library/certification-provider/models/certification-provider';
import { SubentityState } from 'app/projects/subentity/src/lib/models/subentity.state';
import { map } from 'rxjs/operators';
import {
    AddCertificationProviders,
    CreateCertificationProviderSuccess,
    DeleteCertificationProvider,
    DeselectSelectedCertificationProviders,
    FetchCertificatesToReplace,
    FetchCertificationProviders,
    FetchCertificationProvidersFailure,
    FetchCertificationProvidersSearch,
    FetchCertificationProvidersSearchFailure,
    FetchCertificationProvidersSearchSuccess,
    FetchCertificationProvidersSuccess,
    FetchTeamCertificationProvidersSearch,
    FetchTeamMemberCertificationProvidersSearch,
    SearchCertificationProviders,
    SelectFilteredCertificationProviders,
    SetCertificationProviders,
    ToggleSelectedCertificationProvider,
} from '../actions';
import { CertificationProviderApiService } from '../services/certification-provider.api-service';
import { CertificationProviderState } from './certification-provider.state';
import { CertificationProvidersStateModel } from './certification-providers.state-model';

@State<CertificationProvidersStateModel>({
    name: 'certificationProviders',
    defaults: new CertificationProvidersStateModel(),
})
@Injectable()
export class CertificationProvidersState extends SubentityState<CertificationProvider> {
    @Selector([CertificationProviderState.getEntities])
    static getFilteredEntities(state: CertificationProvidersStateModel, providers: CertificationProvider[]): CertificationProvider[] {
        if (!providers) {
            return null;
        }

        return providers
            .filter((item) => state.filtered.indexOf(item.id) !== -1)
            .map((item) => {
                return [state.list.indexOf(item.id), item];
            })
            .sort((a: [number, CertificationProvider], b: [number, CertificationProvider]) => {
                return a[0] > b[0] ? 1 : b[0] > a[0] ? -1 : 0;
            })
            .map((x: [number, CertificationProvider]) => x[1]);
    }

    @Selector()
    static getSelectedEntityIds(state: CertificationProvidersStateModel): string[] {
        return state.selected;
    }

    @Selector()
    static getSearchTerm(state: CertificationProvidersStateModel): string {
        return state.searchTerm;
    }

    @Selector()
    static getTotalCount(state: CertificationProvidersStateModel): number {
        return state.totalCount;
    }

    @Selector()
    static getUserIds(state: CertificationProvidersStateModel): number[] {
        return state.userIds;
    }

    constructor(private store: Store, private _certificationProviderService: CertificationProviderApiService) {
        super(null, 0);
    }

    @Action(AddCertificationProviders)
    addEntities(ctx: StateContext<CertificationProvidersStateModel>, action: AddCertificationProviders): void {
        const providers = this.store.selectSnapshot(CertificationProviderState.getEntities) || [];

        this._patchSubentities(ctx, { payload: { subentityIds: action.payload.map((entity) => entity.id) } }, providers);
    }

    @Action(CreateCertificationProviderSuccess)
    createEntity(ctx: StateContext<CertificationProvidersStateModel>, action: CreateCertificationProviderSuccess): void {
        const providers = this.store.selectSnapshot(CertificationProviderState.getEntities) || [];

        this._patchSubentities(ctx, { payload: { subentityIds: [action.payload.id] } }, providers);
    }

    @Action(DeleteCertificationProvider)
    deleteEntity({ getState, patchState }: StateContext<CertificationProvidersStateModel>, { payload }: DeleteCertificationProvider): void {
        const state = getState();

        patchState({
            list: [...state.list.filter((subentityId) => subentityId !== payload)],
            selected: [...state.selected.filter((subentityId) => subentityId !== payload)],
            filtered: [...state.filtered.filter((subentityId) => subentityId !== payload)],
        });
    }

    @Action(FetchCertificationProviders)
    fetchAll(ctx: StateContext<CertificationProvidersStateModel>, action: FetchCertificationProviders): void {
        ctx.patchState({
            pageSize: action.payload.pageSize,
            pageNumber: action.payload.pageNumber,
        });

        this._fetchSubentities(
            ctx,
            { payload: 'all' },
            SetCertificationProviders,
            FetchCertificationProvidersSuccess,
            FetchCertificationProvidersFailure,
            this._certificationProviderService
                .getCertificationProviders(action.payload.pageSize, action.payload.pageNumber, action.payload.search, action.payload.types, action.payload.statuses)
                .pipe(
                    map((data) => {
                        ctx.patchState({
                            totalCount: data.pagination.number_of_results,
                        });

                        return data.data.map((query) => new CertificationProvider(query));
                    })
                )
        );
    }

    @Action(FetchCertificationProvidersSuccess)
    fetchAllSuccess(ctx: StateContext<CertificationProvidersStateModel>): void {
        this._fetchSubentitiesSuccess(ctx);
    }

    @Action(FetchCertificationProvidersFailure)
    fetchAllFailure(ctx: StateContext<CertificationProvidersStateModel>): void {
        this._fetchSubentitiesFailure(ctx);
    }

    @Action(FetchCertificationProvidersSearch)
    fetchCertificationProvidersSearch(ctx: StateContext<CertificationProvidersStateModel>, action: FetchCertificationProvidersSearch): void {
        ctx.patchState({
            pageSize: action.payload.pageSize,
            pageNumber: action.payload.pageNumber,
        });

        this._fetchSubentities(
            ctx,
            { payload: 'all' },
            SetCertificationProviders,
            FetchCertificationProvidersSearchSuccess,
            FetchCertificationProvidersSearchFailure,
            this._certificationProviderService.getCertificationProvidersSearch(action.payload.pageSize, action.payload.pageNumber, action.payload.search, action.payload.data).pipe(
                map((data) => {
                    ctx.patchState({
                        totalCount: data.pagination.number_of_results,
                        userIds: data.user_ids,
                    });

                    return data.data.map((query) => new CertificationProvider(query));
                })
            )
        );
    }

    @Action(FetchTeamCertificationProvidersSearch)
    fetchTeamCertificationProvidersSearch(ctx: StateContext<CertificationProvidersStateModel>, action: FetchTeamCertificationProvidersSearch): void {
        ctx.patchState({
            pageSize: action.payload.pageSize,
            pageNumber: action.payload.pageNumber,
        });

        this._fetchSubentities(
            ctx,
            { payload: 'all' },
            SetCertificationProviders,
            FetchCertificationProvidersSearchSuccess,
            FetchCertificationProvidersSearchFailure,
            this._certificationProviderService
                .getTeamCertificationProvidersSearch(
                    action.payload.pageSize,
                    action.payload.pageNumber,
                    action.payload.search,
                    action.payload.data,
                    action.payload.teamId,
                    action.payload.includeSubteams
                )
                .pipe(
                    map((data) => {
                        ctx.patchState({
                            totalCount: data.pagination.number_of_results,
                            userIds: data.user_ids,
                        });

                        return data.data.map((query) => new CertificationProvider(query));
                    })
                )
        );
    }

    @Action(FetchTeamMemberCertificationProvidersSearch)
    fetchTeamMemberCertificationProvidersSearch(ctx: StateContext<CertificationProvidersStateModel>, action: FetchTeamMemberCertificationProvidersSearch): void {
        ctx.patchState({
            pageSize: action.payload.pageSize,
            pageNumber: action.payload.pageNumber,
        });

        this._fetchSubentities(
            ctx,
            { payload: 'all' },
            SetCertificationProviders,
            FetchCertificationProvidersSearchSuccess,
            FetchCertificationProvidersSearchFailure,
            this._certificationProviderService
                .getTeamMemberertificationProvidersSearch(action.payload.pageSize, action.payload.pageNumber, action.payload.search, action.payload.data, action.payload.userId)
                .pipe(
                    map((data) => {
                        ctx.patchState({
                            totalCount: data.pagination.number_of_results,
                            userIds: data.user_ids,
                        });

                        return data.data.map((query) => new CertificationProvider(query));
                    })
                )
        );
    }

    @Action(FetchCertificatesToReplace)
    fetchCertificatesToReplace(ctx: StateContext<CertificationProvidersStateModel>, action: FetchCertificatesToReplace): void {
        ctx.patchState({
            pageSize: action.payload.pageSize,
            pageNumber: action.payload.pageNumber,
        });

        this._fetchSubentities(
            ctx,
            { payload: 'all' },
            SetCertificationProviders,
            FetchCertificationProvidersSearchSuccess,
            FetchCertificationProvidersSearchFailure,
            this._certificationProviderService.getCertificatesToReplace(action.payload.pageSize, action.payload.pageNumber, action.payload.certificateId).pipe(
                map((data) => {
                    ctx.patchState({
                        totalCount: data.pagination.number_of_results,
                    });

                    return data.data.map((query) => new CertificationProvider(query));
                })
            )
        );
    }

    @Action(FetchCertificationProvidersSearchSuccess)
    fetchCertificationProvidersSearchSuccess(ctx: StateContext<CertificationProvidersStateModel>): void {
        this._fetchSubentitiesSuccess(ctx);
    }

    @Action(FetchCertificationProvidersSearchFailure)
    fetchCertificationProvidersSearchFailure(ctx: StateContext<CertificationProvidersStateModel>): void {
        this._fetchSubentitiesFailure(ctx);
    }

    @Action(SetCertificationProviders)
    setEntities(ctx: StateContext<CertificationProvidersStateModel>, action: SetCertificationProviders): void {
        this._setSubentities(ctx, action, null);
    }

    @Action(ToggleSelectedCertificationProvider)
    toggleSelect(ctx: StateContext<CertificationProvidersStateModel>, action: ToggleSelectedCertificationProvider): void {
        this._toggleSelectedSubentity(ctx, action);
    }

    @Action(SelectFilteredCertificationProviders)
    selectAll(ctx: StateContext<CertificationProvidersStateModel>): void {
        this._selectFilteredSubentities(ctx);
    }

    @Action(DeselectSelectedCertificationProviders)
    deselectAll(ctx: StateContext<CertificationProvidersStateModel>): void {
        this._deselectSelectedSubentities(ctx);
    }

    @Action(SearchCertificationProviders)
    search(ctx: StateContext<CertificationProvidersStateModel>, action: SearchCertificationProviders): void {
        const providers = this.store.selectSnapshot(CertificationProviderState.getEntities);

        this._searchSubentities(ctx, action, providers);
    }
}
